import type { AfapPhase, AmrapPhase, Challenge, IntervalsPhase } from '@/domain/workout'

/**
 * Reto de agosto 2026. Todo lo que el entrenador publica vive aquí:
 * cambiar una semana es tocar solo este fichero.
 */

const CALENTAMIENTO: IntervalsPhase = {
  id: 'calentamiento',
  kind: 'intervals',
  tag: 'Calentamiento',
  name: 'Calentamiento Tabata',
  summary: '2 rondas · 20/10',
  rounds: 2,
  restSeconds: 10,
  pattern: [
    { exerciseId: 'jumping_jacks', seconds: 20 },
    { exerciseId: 'skippings', seconds: 20 },
    { exerciseId: 'air_squats', seconds: 20 },
    { exerciseId: 'shoulder_taps', seconds: 20 },
  ],
}

const AMRAP_15: AmrapPhase = {
  id: 'amrap-15',
  kind: 'amrap',
  tag: 'AMRAP 15',
  name: 'AMRAP 15 minutos',
  summary: 'Tantas rondas como puedas',
  briefing: 'Tantas rondas como puedas en 15 minutos. El reloj no para: descansa cuando lo necesites.',
  durationSeconds: 15 * 60,
  scored: true,
  movements: [
    { exerciseId: 'air_squats', reps: 10 },
    { exerciseId: 'push_ups', reps: 8 },
    { exerciseId: 'lunges', reps: 12 },
    { exerciseId: 'mountain_climbers', reps: 20 },
  ],
}

const FINISHER: AfapPhase = {
  id: 'finisher',
  kind: 'afap',
  tag: 'Finisher AFAP',
  name: 'Finisher AFAP',
  summary: 'Lo más rápido que puedas',
  briefing:
    'As Fast As Possible. Marca cada bloque al terminarlo y el reloj se para solo al acabar el último.',
  capSeconds: 15 * 60,
  estimatedSeconds: 6 * 60,
  blocks: [
    { exerciseId: 'sit_ups', reps: 20 },
    { exerciseId: 'burpees', reps: 10 },
    { exerciseId: 'sit_ups', reps: 20 },
    { exerciseId: 'burpees', reps: 10 },
  ],
}

export const RETO_AGOSTO_2026: Challenge = {
  id: 'reto-agosto-2026',
  name: 'Reto de agosto',
  targetSessions: 8,
  sessionsPerWeek: 2,
  weeks: [
    {
      number: 1,
      title: { lead: 'Punto', accent: 'de partida' },
      dateLabel: '3 — 9 ago',
      startsOn: '2026-08-03',
      blockLabel: 'AMRAP 15',
      intro: [
        { text: 'Hoy no vienes a hacer tu mejor marca. Vienes a ' },
        { text: 'dejar una marca', strong: true },
        { text: ' y a superarla el 24 de agosto, cuando repitamos este mismo entreno.' },
      ],
      workout: { id: 'semana-1', phases: [CALENTAMIENTO, AMRAP_15, FINISHER] },
    },
    {
      number: 2,
      title: { accent: 'Volumen' },
      dateLabel: '10 — 16 ago',
      startsOn: '2026-08-10',
      blockLabel: '4×40/20',
      intro: [
        { text: 'Ejercicios nuevos y más trabajo total. Aquí no se busca velocidad: se busca ' },
        { text: 'terminar todas las series con buena técnica', strong: true },
        { text: '.' },
      ],
      workout: {
        id: 'semana-2',
        phases: [
          CALENTAMIENTO,
          {
            id: 'intervalos-40-20',
            kind: 'intervals',
            tag: '4 rondas 40/20',
            name: '4 rondas · 40/20',
            summary: 'Cuatro ejercicios, cuatro vueltas',
            rounds: 4,
            restSeconds: 20,
            roundRestSeconds: 60,
            pattern: [
              { exerciseId: 'squat_jump', seconds: 40 },
              { exerciseId: 'pike_push_ups', seconds: 40 },
              { exerciseId: 'glute_bridge', seconds: 40 },
              { exerciseId: 'bicycle_crunch', seconds: 40 },
            ],
          },
          {
            id: 'finisher',
            kind: 'afap',
            tag: 'Finisher AFAP',
            name: 'Finisher AFAP',
            summary: 'Lo más rápido que puedas',
            briefing:
              'As Fast As Possible. Marca cada bloque al terminarlo y el reloj se para solo al acabar el último.',
            capSeconds: 15 * 60,
            estimatedSeconds: 6 * 60,
            blocks: [
              { exerciseId: 'skaters', reps: 30 },
              { exerciseId: 'tricep_dips', reps: 15 },
              { exerciseId: 'skaters', reps: 30 },
              { exerciseId: 'tricep_dips', reps: 15 },
            ],
          },
        ],
      },
    },
    {
      number: 3,
      title: { accent: 'Densidad' },
      dateLabel: '17 — 23 ago',
      startsOn: '2026-08-17',
      blockLabel: '2 × AMRAP 6',
      intro: [
        { text: 'El más duro de los cuatro. Dos bloques cortos, cada uno con su propio reloj. ' },
        { text: 'Si llegas aquí, ya no abandonas.', strong: true },
      ],
      workout: {
        id: 'semana-3',
        phases: [
          CALENTAMIENTO,
          {
            id: 'amrap-a',
            kind: 'amrap',
            tag: 'Bloque A · AMRAP 6',
            name: 'Bloque A · Cadena posterior',
            summary: 'Espalda, glúteo y piernas',
            briefing: 'Tantas rondas como puedas en 6 minutos.',
            durationSeconds: 6 * 60,
            scored: true,
            movements: [
              { exerciseId: 'superman', reps: 15 },
              { exerciseId: 'glute_bridge', reps: 20 },
              { exerciseId: 'inchworm', reps: 8 },
            ],
          },
          {
            id: 'descanso',
            kind: 'countdown',
            tag: 'Descanso',
            name: 'Descanso',
            summary: '90 segundos',
            briefing: 'Respira. Bebe agua.',
            durationSeconds: 90,
          },
          {
            id: 'amrap-b',
            kind: 'amrap',
            tag: 'Bloque B · AMRAP 6',
            name: 'Bloque B · Brazos y core',
            summary: 'Tríceps y abdomen',
            briefing: 'Tantas rondas como puedas en 6 minutos.',
            durationSeconds: 6 * 60,
            movements: [
              { exerciseId: 'tricep_dips', reps: 12 },
              { exerciseId: 'leg_raises', reps: 15 },
              { exerciseId: 'side_plank_dip', reps: 16 },
            ],
          },
          {
            id: 'finisher',
            kind: 'afap',
            tag: 'Finisher AFAP',
            name: 'Finisher AFAP',
            summary: 'Lo más rápido que puedas',
            briefing:
              'As Fast As Possible. Marca cada bloque al terminarlo y el reloj se para solo al acabar el último.',
            capSeconds: 15 * 60,
            estimatedSeconds: 6 * 60,
            blocks: [
              { exerciseId: 'squat_jump', reps: 20 },
              { exerciseId: 'burpees', reps: 10 },
              { exerciseId: 'squat_jump', reps: 20 },
              { exerciseId: 'burpees', reps: 10 },
            ],
          },
        ],
      },
    },
    {
      number: 4,
      title: { accent: 'El test' },
      dateLabel: '24 — 30 ago',
      startsOn: '2026-08-24',
      blockLabel: 'AMRAP 15',
      benchmarkOf: 1,
      intro: [
        { text: 'El mismo entreno de la Semana 1, clavado. ' },
        { text: 'Saca tu marca y supérala.', strong: true },
        { text: ' Esa diferencia es todo lo que ha pasado este mes.' },
      ],
      workout: { id: 'semana-4', phases: [CALENTAMIENTO, AMRAP_15, FINISHER] },
    },
  ],
  rules: [
    {
      title: 'Entero',
      description: 'El entrenamiento completo, de principio a fin. Si lo dejas a medias, no cuenta.',
    },
    {
      title: 'Dos veces por semana',
      description: 'De lunes a domingo, dejando al menos un día de descanso entre las dos.',
    },
    {
      title: 'Foto al grupo',
      description:
        'Una foto tuya al acabar: entrenando o recién terminado. La captura del cronómetro sola no vale. Es lo único que te pido.',
    },
    {
      title: 'Donde estés',
      description: 'La playa, el pueblo, el pasillo de casa. No necesitas material ni gimnasio.',
    },
  ],
  bonus: {
    title: ['Entrena', 'acompañado'],
    maxUses: 2,
    paragraphs: [
      [
        { text: 'Si haces la sesión con alguien que ' },
        { text: 'no entrena con nosotros', strong: true },
        { text: ' y mandáis la foto los dos, esa sesión cuenta ×2 en tu contador.' },
      ],
      [
        {
          text: 'Cuanta más gente de fuera, mejor: de eso va esto. Máximo 2 sesiones dobles en todo el reto.',
        },
      ],
    ],
  },
  prizes: [
    {
      sessions: 8,
      title: 'Septiembre al 50%',
      description: 'Completas las 8 y tu mensualidad de septiembre te sale a mitad de precio.',
    },
    {
      sessions: 6,
      title: 'Una clase gratis',
      description: 'Te quedas cerca y aun así vuelves con una sesión invitada.',
    },
  ],
  footer: ['Volvemos el 2 de septiembre', 'Jardín del Turia · Puente de Ademuz'],
}
